"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_PROJECTION_MONTHS = exports.ReconciledAmountSchema = exports.BILLING_ALERT_STATES = exports.DELINQUENCY_PROGRESSION = exports.BILLING_ROW_STATES = void 0;
exports.projectBillingSchedule = projectBillingSchedule;
exports.reconcileState = reconcileState;
exports.billingScheduleTotals = billingScheduleTotals;
exports.billingScheduleAlerts = billingScheduleAlerts;
exports.addMonths = addMonths;
exports.cashflowByDeal = cashflowByDeal;
exports.cashflowByMonth = cashflowByMonth;
const zod_1 = require("zod");
const pricePhases_1 = require("./pricePhases");
/**
 * THE BILLING SCHEDULE — what a subscription will be charged, month by month,
 * for years ahead, and what it actually was.
 *
 * Two audiences, one table, and they need different halves of it:
 *
 *  - a TENANT sees what they are committed to and what they have paid;
 *  - RUBYTILL sees contracted forward revenue, which is what a lender asks for
 *    when underwriting a business loan against a subscription book.
 *
 * The projection half is derived from `PricePhase[]` (see `pricePhases.ts`) and
 * is therefore the SAME arithmetic that bills — a projection computed any other
 * way is a number that agrees with the invoices only by luck, and a lender is
 * entitled to better than luck.
 *
 * ---------------------------------------------------------------------------
 * TAX, AND THE MISTAKE THAT WOULD FLAG EVERY ROW
 * ---------------------------------------------------------------------------
 *
 * Our phases are EX-TAX: `ensureNetPrice` mints a price for the net amount and
 * Stripe adds tax on top at invoice time. So `invoice.total` is NOT comparable
 * with an expected phase total — on any taxed tenant it is larger by the tax,
 * and a reconciler comparing the two would mark every single row OVERPAID and
 * make the whole feature noise.
 *
 * Reconciliation therefore compares against the invoice SUBTOTAL (ex-tax), and
 * the tax is recorded beside it for the breakdown rather than folded into the
 * comparison. `ReconciledAmountSchema` makes that explicit at the type level so
 * a writer cannot pass a tax-inclusive figure into the field that is compared.
 */
// ---------------------------------------------------------------------------
// A projected row
// ---------------------------------------------------------------------------
exports.BILLING_ROW_STATES = [
    /** In the future. Nothing is owed yet; this is the forward projection. */
    'projected',
    /** The period has begun and Stripe has not reported an outcome yet. */
    'due',
    /** Stripe charged and collected exactly what the schedule said. */
    'paid',
    /** Collected LESS than the schedule said, ex-tax. */
    'underpaid',
    /** Collected MORE than the schedule said, ex-tax. */
    'overpaid',
    /** Stripe attempted and failed, or the invoice is open past its due date. */
    'unpaid',
    /**
     * Escalated: still owed, no longer being retried automatically. A human or a
     * collections process owns it now.
     */
    'collections',
    /**
     * Given up on. Still OWED in principle, but no longer expected to be
     * collected — see `DELINQUENCY_PROGRESSION`.
     */
    'written_off',
    /** Deliberately not billed — the subscription was cancelled before this row. */
    'void',
];
/**
 * WHAT HAPPENS TO A ROW THAT IS NOT PAID.
 *
 * The product pattern, recorded here so the money model accommodates it even
 * though the workflow driving it is not built:
 *
 *     unpaid -> collections -> (device return) -> written_off
 *
 * Only the BILLING states are modelled. A device return is an event on the
 * device agreement, not on a billing row — the hardware goes back, its
 * financing agreement is closed, and the schedule's remaining device lines stop
 * — so it is deliberately absent from this enum rather than squeezed into it.
 * A row can reference the return; it is not a state the row is IN.
 *
 * ⚠️ WHY THIS IS HERE BEFORE THE WORKFLOW IS. These states change what the
 * TOTALS mean, and the totals are already being used — for a loan application.
 * A written-off month that kept counting toward `outstandingCents` would
 * overstate collectable revenue, which is the single number a lender is most
 * entitled to have right. Adding the states later would mean restating figures
 * that had already been shown to someone.
 */
exports.DELINQUENCY_PROGRESSION = [
    'unpaid', 'collections', 'written_off',
];
/**
 * The states an operator must act on.
 *
 * `written_off` is NOT one: it is a decision already taken, and a permanent
 * alert on a closed matter trains people to ignore the list. It stays visible
 * in the table and in its own total.
 */
exports.BILLING_ALERT_STATES = new Set([
    'underpaid', 'overpaid', 'unpaid', 'collections',
]);
/**
 * WHAT STRIPE ACTUALLY DID, as recorded from an invoice webhook.
 *
 * `subtotalCents` is the figure compared against the schedule — ex-tax, because
 * the schedule is ex-tax. `taxCents` and `totalCents` are carried for the
 * customer's breakdown and are deliberately NOT what reconciliation reads.
 */
exports.ReconciledAmountSchema = zod_1.z.object({
    /** EX-TAX. The only field reconciliation compares. */
    subtotalCents: zod_1.z.number().int(),
    taxCents: zod_1.z.number().int().min(0).default(0),
    /** What the customer was actually charged, tax included. Display only. */
    totalCents: zod_1.z.number().int(),
    /** What Stripe collected. Differs from `totalCents` on a partial payment. */
    amountPaidCents: zod_1.z.number().int().min(0),
    currency: zod_1.z.string().default('usd'),
    invoiceId: zod_1.z.string().optional(),
    invoiceUrl: zod_1.z.string().optional(),
    paidAt: zod_1.z.number().optional(),
    /**
     * Stripe's own status, verbatim (`paid`, `open`, `uncollectible`, `void`).
     * Kept raw so an unfamiliar value is visible rather than mapped into
     * something convenient and wrong.
     */
    stripeStatus: zod_1.z.string().optional(),
});
// ---------------------------------------------------------------------------
// Projection
// ---------------------------------------------------------------------------
/** Five years of monthly cycles — what a lender asks for. */
exports.DEFAULT_PROJECTION_MONTHS = 60;
/**
 * The forward schedule, one row per billing cycle.
 *
 * Rows past the last phase boundary all carry the OPEN-ENDED phase's amount,
 * which is exactly what makes a five-year projection possible from a schedule
 * that Stripe itself only describes until its final phase: the subscription
 * continues at that price until someone cancels it.
 *
 * ⚠️ A PROJECTION, NOT A PROMISE. It assumes the tenant does not cancel, does
 * not add or remove packages, and does not change locations. Any surface
 * showing it to a lender must say so — it is contracted price × assumed
 * survival, not recognised revenue.
 *
 * A schedule whose every line has ended (all hardware paid off, no software)
 * produces rows at 0 rather than stopping, so the table's length is always the
 * period asked for and a caller never has to distinguish "no row" from "no
 * charge".
 */
function projectBillingSchedule(args) {
    const months = Math.max(0, Math.floor(args.months ?? exports.DEFAULT_PROJECTION_MONTHS));
    const elapsed = Math.max(0, Math.floor(args.elapsedMonths ?? 0));
    const rows = [];
    for (let month = 0; month < months; month++) {
        const phase = (0, pricePhases_1.expectedAtMonth)(args.phases, month);
        const expectedCents = phase ? phase.totalCents : 0;
        const lines = phase
            ? phase.lines.map((l) => ({ key: l.key, quantity: l.quantity, amountCents: l.amountCents }))
            : [];
        const actual = args.actuals?.[month];
        const row = {
            ...(args.tenantId ? { tenantId: args.tenantId } : {}),
            ...(args.dealCode ? { dealCode: args.dealCode } : {}),
            ...(args.locationId ? { locationId: args.locationId } : {}),
            month,
            ...(typeof args.startedAt === 'number' ? { periodStart: addMonths(args.startedAt, month) } : {}),
            expectedCents,
            lines,
            state: 'projected',
            ...(actual ? { actual } : {}),
        };
        if (typeof args.cancelledAtMonth === 'number' && month >= args.cancelledAtMonth) {
            // Cancelled: nothing is owed from here, and a projection that kept
            // counting revenue past a cancellation is the one error a lender would
            // most object to.
            row.state = 'void';
            row.expectedCents = 0;
            row.lines = [];
        }
        else if (args.states?.[month]) {
            // An OPERATOR DECISION overrides what the invoice status implies: a row
            // escalated to collections or written off stays there even though Stripe
            // still reports its invoice as merely `open`. Recomputing from the
            // invoice would silently undo the decision on every projection.
            row.state = args.states[month];
            if (actual)
                row.differenceCents = actual.subtotalCents - expectedCents;
        }
        else if (actual) {
            row.differenceCents = actual.subtotalCents - expectedCents;
            row.state = reconcileState(expectedCents, actual);
        }
        else if (month < elapsed) {
            row.state = 'due';
        }
        rows.push(row);
    }
    return rows;
}
/**
 * The state one row is in, given what Stripe reported.
 *
 * Compares the EX-TAX subtotal, never the total — see the header note. An
 * invoice Stripe has not marked `paid` is `unpaid` whatever its amounts say: a
 * correct-looking amount on an uncollected invoice is not money.
 */
function reconcileState(expectedCents, actual) {
    const status = String(actual.stripeStatus ?? '');
    if (status === 'void')
        return 'void';
    if (status && status !== 'paid')
        return 'unpaid';
    // Stripe says paid but collected nothing — treat as unpaid rather than as a
    // very large underpayment, because the operator's action is different.
    if (actual.amountPaidCents <= 0 && expectedCents > 0)
        return 'unpaid';
    const difference = actual.subtotalCents - expectedCents;
    if (difference === 0)
        return 'paid';
    return difference < 0 ? 'underpaid' : 'overpaid';
}
function billingScheduleTotals(rows) {
    const totals = {
        projectedCents: 0, collectedCents: 0, outstandingCents: 0, writtenOffCents: 0,
        taxCollectedCents: 0, alertCount: 0, netDifferenceCents: 0, months: 0,
    };
    for (const row of rows || []) {
        if (row.state === 'void')
            continue;
        totals.months += 1;
        totals.projectedCents += row.expectedCents;
        if (row.actual) {
            totals.taxCollectedCents += row.actual.taxCents || 0;
            if (row.state === 'paid' || row.state === 'underpaid' || row.state === 'overpaid') {
                totals.collectedCents += row.actual.subtotalCents;
            }
        }
        if (row.state === 'due' || row.state === 'unpaid' || row.state === 'collections') {
            totals.outstandingCents += row.expectedCents;
        }
        if (row.state === 'written_off')
            totals.writtenOffCents += row.expectedCents;
        if (exports.BILLING_ALERT_STATES.has(row.state))
            totals.alertCount += 1;
        if (typeof row.differenceCents === 'number')
            totals.netDifferenceCents += row.differenceCents;
    }
    return totals;
}
/** Only the rows an operator must act on — what an alerts panel lists. */
function billingScheduleAlerts(rows) {
    return (rows || []).filter((r) => exports.BILLING_ALERT_STATES.has(r.state));
}
/**
 * Add whole months to a timestamp, clamping the day so month-ends behave.
 *
 * A subscription that starts on the 31st bills on the 30th in April and the
 * 28th in February — Stripe clamps, and a projection that produced "31 April"
 * would drift a day out from the invoice dates for the rest of the schedule.
 * UTC throughout: these are billing periods, not wall-clock local dates, and
 * mixing the two is how a row lands in the wrong month for a tenant in a
 * negative-offset timezone.
 */
function addMonths(startedAt, months) {
    const d = new Date(startedAt);
    const day = d.getUTCDate();
    const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1, d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds()));
    const daysInTargetMonth = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(day, daysInTargetMonth));
    return target.getTime();
}
/**
 * Group schedule rows by the deal they were sold on.
 *
 * The shape the future revenue/cashflow report reads. Pure, and deliberately
 * takes ROWS rather than a query: whatever assembles them — a table scan, a
 * report job, a single tenant's page — the aggregation is one implementation,
 * so a per-deal figure quoted in a report is the same figure the tenant's own
 * table adds up to.
 *
 * Rows with no `dealCode` are grouped under `''` — a direct signup is a real
 * cohort and dropping it would make the report's total disagree with the
 * platform's. A caller labels it however it likes.
 */
function cashflowByDeal(rows) {
    const byDeal = new Map();
    for (const row of rows || []) {
        const key = row.dealCode || '';
        const list = byDeal.get(key);
        if (list)
            list.push(row);
        else
            byDeal.set(key, [row]);
    }
    const out = [];
    for (const [dealCode, dealRows] of byDeal) {
        out.push({
            dealCode,
            totals: billingScheduleTotals(dealRows),
            tenantCount: new Set(dealRows.map((r) => r.tenantId).filter(Boolean)).size,
        });
    }
    // Largest contracted value first — the order a revenue report is read in.
    return out.sort((a, b) => b.totals.projectedCents - a.totals.projectedCents);
}
/**
 * Expected and collected per CALENDAR month across many schedules — the
 * cashflow curve, as against the per-deal totals above.
 *
 * Keyed by the row's `month` index rather than by date, because subscriptions
 * start on different days and a lender asks "what does month N of a cohort
 * look like", not "what landed in March".
 */
function cashflowByMonth(rows) {
    const byMonth = new Map();
    for (const row of rows || []) {
        if (row.state === 'void')
            continue;
        const entry = byMonth.get(row.month) || { month: row.month, expectedCents: 0, collectedCents: 0, alertCount: 0 };
        entry.expectedCents += row.expectedCents;
        if (row.actual && (row.state === 'paid' || row.state === 'underpaid' || row.state === 'overpaid')) {
            entry.collectedCents += row.actual.subtotalCents;
        }
        if (exports.BILLING_ALERT_STATES.has(row.state))
            entry.alertCount += 1;
        byMonth.set(row.month, entry);
    }
    return [...byMonth.values()].sort((a, b) => a.month - b.month);
}
//# sourceMappingURL=billingSchedule.js.map